import config from '../../config.js';

import { SHIPPING_DESTINATION_COUNTRIES } from '../App/AppSchema.js';
import { getProductSlug } from './ProductActions.js';

// Shared shipping / return policy blocks so the product page and the category
// listing (ItemList) expose the exact same Offer structure to search engines.
// The destination list lives in AppSchema so the Offer and the Organization areaServed
// can never drift apart.

const RETURN_POLICY_COUNTRIES = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'];

function getOfferShippingDetails(product) {
	return {
		'@type': 'OfferShippingDetails',
		shippingRate: {
			'@type': 'MonetaryAmount',
			value: '0',
			currency: 'EUR',
		},
		deliveryTime: {
			'@type': 'ShippingDeliveryTime',
			transitTime: {
				'@type': 'QuantitativeValue',
				minValue: '2',
				maxValue: '5',
				unitCode: 'd',
			},
			handlingTime: {
				'@type': 'QuantitativeValue',
				minValue: '0',
				maxValue: '1',
				unitCode: 'd',
			},
			cutoffTime: {
				'@type': 'QuantitativeValue',
				value: '10:00:00Z',
			},
			businessDays: {
				'@type': 'OpeningHoursSpecification',
				dayOfWeek: [
					'https://schema.org/Monday',
					'https://schema.org/Tuesday',
					'https://schema.org/Wednesday',
					'https://schema.org/Thursday',
					'https://schema.org/Friday',
				],
			},
		},
		weight: {
			'@type': 'QuantitativeValue',
			value: product.weightNet || product.weight,
			unitCode: 'kg',
		},
		shippingDestination: SHIPPING_DESTINATION_COUNTRIES.map(addressCountry => ({
			'@type': 'DefinedRegion',
			addressCountry,
		})),
	};
}

function getMerchantReturnPolicy() {
	return {
		'@type': 'MerchantReturnPolicy',
		applicableCountry: RETURN_POLICY_COUNTRIES,
		returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
		merchantReturnDays: 14,
		returnMethod: 'https://schema.org/ReturnByMail',
		returnFees: 'https://schema.org/ReturnShippingFees',
		returnShippingFeesAmount: {
			'@type': 'MonetaryAmount',
			value: '9.90',
			currency: 'EUR',
		},
	};
}

// Stable @id for the producer entity, so every page can reference the same Organization node
// instead of redefining it. Deliberately anchored on OUR domain, never on the producer's:
// an @id (or url) pointing at keesbo.eu would tell search engines and LLMs that the authoritative
// entity lives there, routing buyers straight to the supplier.
export const PRODUCER_KEESBO_ID = `${config.url}#producer`;

// Minimal Organization node for the salaison that manufactures the charcuterie.
// Commercially sensitive fields are intentionally omitted: no `url` (outbound link to the
// supplier's shop), no `legalName` and no street address — a legal name plus an address is a
// ready-made sourcing lead for wholesale buyers. Region-level provenance is enough to support
// the E-E-A-T "Who" and origin claims; Google never required a purchase path to the supplier.
export function getProducerOrganizationSchema() {
	return {
		'@type': 'Organization',
		'@id': PRODUCER_KEESBO_ID,
		name: 'Keesbo',
		address: {
			'@type': 'PostalAddress',
			addressRegion: 'Catalogne',
			addressCountry: 'ES',
		},
	};
}

// Product.brand: reference the shared producer node by @id when the product is made by Keesbo,
// otherwise emit a minimal Organization from the product's own brand field. Never guesses a brand.
export function getProductBrandSchema(product) {
	const { brand } = product || {};
	if(!brand) {
		return null;
	}
	return brand === 'Keesbo' ? { '@id': PRODUCER_KEESBO_ID } : { '@type': 'Organization', name: brand };
}

// Builds the full Offer JSON-LD object (including shippingDetails and
// hasMerchantReturnPolicy) shared between the product page and the category listing.
export function getProductOfferSchema(product, { locale, price, priceValidUntil = null, url = null } = {}) {
	const offerUrl = url || `${config.url}${locale}/${getProductSlug(product, locale)}`;
	return {
		'@type': 'Offer',
		price: `${price}`,
		priceCurrency: 'EUR',
		availability: 'https://schema.org/InStock',
		...(priceValidUntil ? { priceValidUntil } : {}),
		url: offerUrl,
		shippingDetails: getOfferShippingDetails(product),
		hasMerchantReturnPolicy: getMerchantReturnPolicy(),
	};
}
